/**
 * Echo Hunt -- the real (network-backed) LLM transport for `CallLLM` (P3).
 *
 * This is the ONLY file in `Source/EchoHunt/` besides other `*_Live.ts` wrappers that is allowed to
 * touch the `store` (for the user's API key/API-type settings) or the shared LLM helpers. The pure
 * modules take an `LLMBackend` as a parameter, so tests use `MockLLMBackend` instead of this.
 *
 * See §16.0 "LLM execution" in `Docs/Plans/Others/2026-10-19_EchoHunt.md`.
 */
import {store} from "../Store/index.js";
import {LLMApi} from "../Store/main/settings.js";
import {StoryMessageProto} from "../Server/DB/storyMessages/@StoryMessage.js";
import {GenerateNextMessage_Generic} from "../Utils/AI/LLMs/@Shared.js";
import {LLMBackend, LLMRequest, LLMResponse} from "./LLM.js";
import {IsReasoningMandatoryError, ResolveReasoningEffort} from "./ModelCapabilities.js";
import {GetModelCapabilities} from "./ModelCapabilities_Live.js";
import {ReasoningEffort, ResolveReasoningForRole} from "./Roles.js";
import {GetMyEchoProfile} from "./Profiles_Live.js";
import {EffortLevel, ThinkingType} from "../Server/DB/engineConfigs/@EngineConfig/@EC_StoryExplore.js";

/** `ReasoningEffort` and `EffortLevel` are separate declarations (the pure one cannot import the other -- see
 * `Roles.ts`), but they carry identical identifiers, so this is a rename rather than a conversion. */
function ToEffortLevel(effort: ReasoningEffort): EffortLevel {
	return effort as unknown as EffortLevel;
}

/** The user's per-role effort choice, read from the profile. Returns null for "auto".
 *
 * Deliberately tolerant of a COLD read: `GetMyEchoProfile` throws a mobx-graphlink `BailError` when its data has
 * not arrived, which is normally caught by a React `observer` -- but a pipeline call runs from a click handler, and
 * an uncaught bail there would fail the round over a settings lookup. A missing profile simply means "auto". */
function RequestedEffortForRole(role: LLMRequest["role"]): ReasoningEffort|n {
	try {
		return ResolveReasoningForRole(GetMyEchoProfile()?.modelReasoning, role);
	} catch {
		return null;
	}
}

/** Default temperature for Echo Hunt calls. Deterministic-ish, but not so low that the generator
 * collapses into repeating phrasings across bullets (§5.5 anti-cliché). */
export const ECHO_HUNT_DEFAULT_TEMPERATURE = 1;

/** Network-backed backend: routes through `GenerateNextMessage_Generic`, which honors the user's
 * API-type/key/model settings (OpenRouter or Anthropic). */
export class LiveLLMBackend implements LLMBackend {
	constructor(options?: {temperature?: number, maxOutputTokens?: number}) {
		this.temperature = options?.temperature ?? ECHO_HUNT_DEFAULT_TEMPERATURE;
		this.maxOutputTokens = options?.maxOutputTokens;
	}

	readonly temperature: number;
	readonly maxOutputTokens: number|n;

	async call(request: LLMRequest): Promise<LLMResponse> {
		const settings = store.main.settings;
		const modelOverride = request.model.trim().length > 0 ? request.model.trim() : null;
		const maxOutputTokens = request.maxOutputTokens ?? this.maxOutputTokens;
		const model = modelOverride ?? (settings.llm_mainAPI == LLMApi.openRouter ? settings.openRouterModel : settings.anthropicModel);

		// 2026-10-21: some models refuse a request that disables reasoning ("Reasoning is mandatory for this
		// endpoint and cannot be disabled"). Consult the model's metadata first; for every model where reasoning
		// is optional (or unknown), nothing is sent and the call is byte-identical to before.
		const capabilities = await GetModelCapabilities(model);
		// the request may name an effort explicitly (a test call, or a caller experimenting with one); otherwise the
		// role's stored setting decides, and "auto" is resolved from the model's own metadata
		const requested = request.reasoningEffort ?? RequestedEffortForRole(request.role);
		const resolved = ResolveReasoningEffort(capabilities, {requested});

		let text = "";
		let outputTokens: number|n = null;

		const run = async(effort: ReasoningEffort|null)=>{
			await GenerateNextMessage_Generic({
				minNumberedListSize: 999, // Echo Hunt never uses numbered-list choice lists
				dynamicListNumbers: [],
				llmHistory_keepLastXNumberedLists: 0,
				temperature: request.temperature ?? this.temperature,
				// model override: only set when the role has one, so the global model (and its provider
				// list / block-training flags) stays in effect otherwise
				...(modelOverride != null ? {model: modelOverride} : {}),
				...(maxOutputTokens != null ? {responseTokens: maxOutputTokens} : {}),
				// reasoning is only touched when it is actually needed (see above)
				...(effort != null ? {thinkingType: ThinkingType.adaptive, effort: ToEffortLevel(effort)} : {}),

				systemPrompt: request.system ?? undefined,
				prevMessages: [new StoryMessageProto({role: "user", content: request.user, model: null})],
				onTextSegment: nextTextSegment=>{
					text += nextTextSegment;
				},
				onOutputTokens: tokens=>{
					outputTokens = tokens;
				},
			});
		};

		try {
			await run(resolved.reasoningRequired ? resolved.effort : null);
		} catch (ex) {
			// A DIRECT-Anthropic call has no metadata endpoint to consult, so the model's requirement can only be
			// discovered here. One retry, with reasoning on; anything else propagates unchanged.
			if (resolved.reasoningRequired || !IsReasoningMandatoryError(`${ex}`)) throw ex;
			afterMandatoryReasoningRetry?.();
			text = "";
			outputTokens = null;
			await run("high");
		}

		return {
			text,
			outputTokens,
			model,
		};
	}
}

/** Injectable hook fired when the mandatory-reasoning fallback runs, so the Settings tab can say "that model
 * needs reasoning; it was switched on automatically" instead of leaving the user to wonder why the first call
 * failed. Null in production; the panel sets it while a Test is in flight. */
export let afterMandatoryReasoningRetry: (()=>void)|null = null;
export function SetMandatoryReasoningRetryHook(hook: (()=>void)|null) {
	afterMandatoryReasoningRetry = hook;
}

/** Shared singleton, so repeated pipeline calls reuse the same SDK clients (which is required by the
 * OpenRouter/Anthropic helpers -- they assert the API key hasn't changed mid-session). */
export const liveLLMBackend = new LiveLLMBackend();
